import { Box, Link, Paper, Stack, Typography } from '@mui/material';
import React from 'react';

// Helper para pegar iniciais
const getInitials = (name: string) => {
  if (!name) return 'AI';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const AvatarGradient = ({ name }: { name: string }) => {
  const initials = getInitials(name);
  return (
    <Box
      sx={{
        width: 64,
        height: 64,
        borderRadius: '50%',
        background:
          'linear-gradient(135deg, #B4EC51 0%, #429321 50%, #396afc 100%)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        mr: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 700,
          fontSize: 22,
          fontFamily: 'Space Grotesk',
        }}
      >
        {initials}
      </Typography>
    </Box>
  );
};

interface Goal {
  title: string;
  description: string;
}

interface CardsAssistantProps {
  name: string;
  email: string;
  goals: Goal[];
}

export const CardsAssistant = ({ name, email, goals }: CardsAssistantProps) => {
  return (
    <Stack
      spacing={2}
      sx={{
        maxWidth: 630,
        width: '70%',
        '@keyframes fadeInUp': {
          '0%': {
            opacity: 0,
            transform: 'translateY(50px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
    >
      {/* Card 1: Assistant name */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          p: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 0,
          backgroundColor: '#fff',
          boxShadow: '0px 10px 20px 10px rgba(0, 0, 0, 0.05)',
          opacity: 0,
          animation: 'fadeInUp 0.8s ease-out 0.3s both',
        }}
      >
        <AvatarGradient name={name} />
        <Box>
          <Typography fontWeight={500} fontSize={16} color="#222">
            Assistant name
          </Typography>
          <Typography fontWeight={400} fontSize={15} mt={0.75} color="#424242">
            Oppy |{' '}
            <Link
              href="https://www.oppy.pro/"
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              color="#424242"
            >
              https://www.oppy.pro/
            </Link>
          </Typography>
          <Typography fontSize={14} color="#888" mt={0.5}>
            {email || 'assistantemail@open.oppy.pro'}
          </Typography>
        </Box>
      </Paper>

      {/* Card 2: Business description */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          p: 4,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          backgroundColor: '#fff',
          boxShadow: '0px 10px 20px 10px rgba(0, 0, 0, 0.05)',
          flexDirection: 'column',
          opacity: 0,
          animation: 'fadeInUp 0.8s ease-out 0.5s both',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img
            src="/icon/case.svg"
            alt="Business description"
            style={{ width: 20, height: 20 }}
          />
          <Typography
            fontWeight={400}
            fontSize={17}
            fontFamily={'Space Grotesk'}
            color="#222"
          >
            Business description
          </Typography>
        </Box>
        <Box>
          <Typography fontSize={13} color="#444" lineHeight={1.6}>
            Oppy is an AI-powered platform that creates personalized AI
            assistants, called "Oppies", for real estate professionals. These AI
            assistants work 24/7 to handle routine tasks like lead qualification
            and follow-ups.
          </Typography>
        </Box>
      </Paper>

      {/* Card 3: Assistant goals */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          p: 4,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 3,
          backgroundColor: '#fff',
          boxShadow: '0px 10px 20px 10px rgba(0, 0, 0, 0.05)',
          flexDirection: 'column',
          opacity: 0,
          animation: 'fadeInUp 0.8s ease-out 0.7s both',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <img
            src="/icon/flag.svg"
            alt="Assistant goals"
            style={{ width: 20, height: 20 }}
          />
          <Typography
            fontWeight={400}
            fontSize={17}
            fontFamily={'Space Grotesk'}
            color="#222"
          >
            Assistant goals
          </Typography>
        </Box>
        <Box>
          {goals &&
            goals.map((goal, idx) => (
              <Box key={goal.title} mb={idx < goals.length - 1 ? 1.5 : 0}>
                <Typography fontWeight={600} fontSize={14} color="#222">
                  {goal.title}
                </Typography>
                <Typography fontSize={13} color="#444" lineHeight={1.5}>
                  {goal.description}
                </Typography>
              </Box>
            ))}
        </Box>
      </Paper>
    </Stack>
  );
};
